<template>
  <modal :show.sync="show" :on-close="onClose" >
    <div v-if="!uploadAction" class="column content-box">
      <woot-modal-header :header-title="$t('IMPORT_CONTACTS.TITLE')">
        <p>
          {{ $t('IMPORT_CONTACTS.DESC') + ' ' }}
          <a class="btn-primary" :href="csvUrl" download="import-contacts-sample">CSV |</a>
          <a class="btn-primary" :href="xlsxUrl" download="import-contacts-sample">XLSX |</a>
          <a class="btn-primary" :href="xlsUrl" download="import-contacts-sample">XLS</a>
        </p>
      </woot-modal-header>
      <div class="row modal-content">
        <div class="card-input-file">
          <span>{{ $t('IMPORT_CONTACTS.FORM.LABEL') + ' ' + 'CSV, XLSX, XLS' }}</span>
          <input
            ref="file"
            type="file"
            accept="text/csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            @change="handleFileUpload"
            class="input-file"
          />
          <p v-if="isValidating" class="validation-status">
            {{ $t('IMPORT_CONTACTS.ERROR_UPLOAD.VALIDATING') }}
          </p>
        </div>
        <div v-if="fileErrors.length" class="validation-errors medium-12 columns">
          <p class="validation-errors-title">
            {{ $t('IMPORT_CONTACTS.ERROR_UPLOAD.TITLE') }}
            <span class="validation-errors-count">
              ({{ fileErrors.length }})
            </span>
          </p>
          <ul class="validation-errors-list">
            <li
              v-for="(error, index) in visibleFileErrors"
              :key="index"
              class="validation-errors-item"
            >
              {{ error }}
            </li>
          </ul>
          <p v-if="hiddenErrorsCount > 0" class="validation-errors-more">
            {{
              $t('IMPORT_CONTACTS.ERROR_UPLOAD.MORE_ERRORS', {
                count: hiddenErrorsCount,
              })
            }}
          </p>
          <p class="validation-errors-hint">
            {{ $t('IMPORT_CONTACTS.ERROR_UPLOAD.HINT') }}
          </p>
        </div>
        <div class="medium-12 columns">
          <label>
            <span>{{ $t('IMPORT_CONTACTS.WALLET.LABEL') }}</span>
          </label>
          <multiselect-dropdown
            :options="agentsWallets"
            :selected-item="agentSelectedWallet"
            :multiselector-title="$t('IMPORT_CONTACTS.WALLET.MULTISELECTOR')"
            :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
            :no-search-result="$t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.AGENT')"
            :input-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT')"
            @click="getAgentWalletId"
          />
        </div>
        <div class="medium-12 columns import-label-section">
          <label class="import-label-header">
            <span>{{ $t('IMPORT_CONTACTS.LABEL.LABEL') }}</span>
            <span class="import-label-optional">{{
              $t('IMPORT_CONTACTS.LABEL.OPTIONAL')
            }}</span>
          </label>
          <multiselect-dropdown
            :options="labelOptions"
            :selected-item="selectedLabel"
            :has-thumbnail="false"
            :multiselector-title="$t('IMPORT_CONTACTS.LABEL.MULTISELECTOR')"
            :multiselector-placeholder="$t('IMPORT_CONTACTS.LABEL.NONE')"
            :no-search-result="$t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.AGENT')"
            :input-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT')"
            @click="onLabelSelect"
          />
          <p class="import-label-help">
            <fluent-icon icon="tag" size="12" class="import-label-help-icon" />
            {{ $t('IMPORT_CONTACTS.LABEL.HELP') }}
          </p>
        </div>
        <div class="medium-12 columns duplicate-strategy">
          <label class="duplicate-strategy-label">
            <span>{{ $t('IMPORT_CONTACTS.DUPLICATE_STRATEGY.LABEL') }}</span>
          </label>
          <div class="duplicate-strategy-options">
            <label
              v-for="option in duplicateStrategyOptions"
              :key="option.value"
              class="duplicate-strategy-option"
              :class="{ 'duplicate-strategy-option--selected': duplicateStrategy === option.value }"
            >
              <input
                v-model="duplicateStrategy"
                type="radio"
                name="duplicate-strategy"
                :value="option.value"
              />
              <div class="duplicate-strategy-content">
                <span class="duplicate-strategy-title">{{ option.label }}</span>
                <span class="duplicate-strategy-desc">{{ option.description }}</span>
              </div>
            </label>
          </div>
        </div>
        <div class="modal-footer">
          <div class="medium-12 columns">
            <woot-button
              :disabled="!canSubmitImport"
              :loading="uiFlags.isCreating || isValidating"
              @click="uploadFile"
              class="bg-primary"
            >
              {{ $t('IMPORT_CONTACTS.FORM.SUBMIT') }}
            </woot-button>
            <button class="button clear btn-primary" @click.prevent="onClose">
              {{ $t('IMPORT_CONTACTS.FORM.CANCEL') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="isProcessing" class="container-icons processing-content">
      <woot-spinner />
      <p class="processing-message">{{ $t('IMPORT_CONTACTS.PROCESSING') }}</p>
    </div>
    <div v-else-if="importSummary" class="summary-container">
      <div class="summary-header">
        <fluent-icon
          :icon="summaryIcon.name"
          type="solid"
          size="48"
          class="summary-icon"
          :class="`summary-icon--${summaryIcon.variant}`"
        />
        <h3 class="summary-title">{{ summaryTitle }}</h3>
        <p class="summary-subtitle">{{ summarySubtitle }}</p>
      </div>

      <div class="summary-stats">
        <div
          v-for="stat in summaryStats"
          :key="stat.key"
          class="summary-stat"
          :class="`summary-stat--${stat.variant}`"
        >
          <span class="summary-stat-value">{{ stat.count }}</span>
          <span class="summary-stat-label">{{ stat.label }}</span>
        </div>
      </div>

      <div class="summary-actions">
        <a
          v-if="importSummary.error_records > 0 && importSummary.failed_records_url"
          :href="importSummary.failed_records_url"
          target="_blank"
          class="summary-download-link"
        >
          {{ $t('IMPORT_CONTACTS.SUMMARY.DOWNLOAD_ERRORS') }}
        </a>
        <button class="button clear btn-primary summary-close" @click.prevent="onClose">
          {{ $t('IMPORT_CONTACTS.SUMMARY.CLOSE') }}
        </button>
      </div>
    </div>
    <div v-else>
      <div v-if="fileErrors.length" class="error-container medium-12 columns container-icons">
        <img :src="renderSvgAction" />
        <div class="container-message">
          <p class="error-title">{{ renderMessageAction }}</p>
          <ul class="error-list">
            <li
              v-for="(error, index) in fileErrors"
              :key="index"
              class="error-item"
            >
              {{ error }}
            </li>
          </ul>
        </div>
      </div>
      <div class="container-icons error-content" v-else>
        <img :src="renderSvgAction" />
        <woot-modal-header :header-content="renderMessageAction" />
      </div>
    </div>
  </modal>
</template>

<script>
import Modal from '../../../../components/Modal';
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import { CONTACTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import ContactAPI from '../../../../api/contacts';
import XLSX from 'xlsx';
import Papa from 'papaparse';
import svgError from '../../../../../../../public/integrations/catalog/error-close-svgrepo-com.svg';
import svgSuccess from '../../../../../../../public/integrations/catalog/check-svgrepo-com.svg';

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 100;
const MAX_VISIBLE_ERRORS = 20;

export default {
  components: {
    Modal,
    MultiselectDropdown,
  },
  mixins: [alertMixin],
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      errorUpload: false,
      uploadAction: false,
      isProcessing: false,
      isValidating: false,
      importSummary: null,
      pollingTimer: null,
      show: true,
      file: '',
      page: 1,
      fileErrors: [],
      duplicateStrategy: 'ignore',
      selectedWalletId: null,
      selectedLabelId: null,
      selectedLabel: {
        id: null,
        name: 'Nenhum marcador',
      },
      agentSelectedWallet: {
        confirmed: true,
        name: 'Nenhum',
        id: null,
        role: 'agent',
        account_id: 0,
        email: 'None',
        wallet: null,
      },
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      agentList: 'agents/getAgents',
      allLabels: 'labels/getLabels',
    }),
    canSubmitImport() {
      return (
        Boolean(this.file) &&
        !this.uiFlags.isCreating &&
        !this.isValidating &&
        this.fileErrors.length === 0
      );
    },
    visibleFileErrors() {
      return this.fileErrors.slice(0, MAX_VISIBLE_ERRORS);
    },
    hiddenErrorsCount() {
      return Math.max(this.fileErrors.length - MAX_VISIBLE_ERRORS, 0);
    },
    duplicateStrategyOptions() {
      return [
        {
          value: 'ignore',
          label: this.$t('IMPORT_CONTACTS.DUPLICATE_STRATEGY.IGNORE'),
          description: this.$t('IMPORT_CONTACTS.DUPLICATE_STRATEGY.IGNORE_DESC'),
        },
        {
          value: 'update',
          label: this.$t('IMPORT_CONTACTS.DUPLICATE_STRATEGY.UPDATE'),
          description: this.$t('IMPORT_CONTACTS.DUPLICATE_STRATEGY.UPDATE_DESC'),
        },
      ];
    },
    summaryStats() {
      if (!this.importSummary) return [];

      return [
        {
          key: 'created',
          count: this.importSummary.created_records || 0,
          label: this.$t('IMPORT_CONTACTS.SUMMARY.CREATED_LABEL'),
          variant: 'success',
        },
        {
          key: 'updated',
          count: this.importSummary.updated_records || 0,
          label: this.$t('IMPORT_CONTACTS.SUMMARY.UPDATED_LABEL'),
          variant: 'info',
        },
        {
          key: 'ignored',
          count: this.importSummary.ignored_records || 0,
          label: this.$t('IMPORT_CONTACTS.SUMMARY.IGNORED_LABEL'),
          variant: 'neutral',
        },
        {
          key: 'errors',
          count: this.importSummary.error_records || 0,
          label: this.$t('IMPORT_CONTACTS.SUMMARY.ERRORS_LABEL'),
          variant: 'danger',
        },
      ];
    },
    summaryIcon() {
      if (!this.importSummary) {
        return { name: 'checkmark-circle', variant: 'success' };
      }

      if (this.importSummary.status === 'failed') {
        return { name: 'dismiss-circle', variant: 'failed' };
      }

      if (this.importSummary.error_records > 0) {
        return { name: 'warning', variant: 'warning' };
      }

      return { name: 'checkmark-circle', variant: 'success' };
    },
    summaryTitle() {
      if (!this.importSummary) return '';

      if (this.importSummary.status === 'failed') {
        return this.$t('IMPORT_CONTACTS.SUMMARY.TITLE_FAILED');
      }

      if (this.importSummary.error_records > 0) {
        return this.$t('IMPORT_CONTACTS.SUMMARY.TITLE_PARTIAL');
      }

      return this.$t('IMPORT_CONTACTS.SUMMARY.TITLE');
    },
    summarySubtitle() {
      if (!this.importSummary) return '';

      if (this.importSummary.status === 'failed') {
        return this.importSummary.processing_errors || this.$t('IMPORT_CONTACTS.SUMMARY.SUBTITLE_FAILED');
      }

      if (this.importSummary.error_records > 0) {
        return this.$t('IMPORT_CONTACTS.SUMMARY.SUBTITLE_PARTIAL');
      }

      return this.$t('IMPORT_CONTACTS.SUMMARY.SUBTITLE');
    },
    renderSvgAction() {
      return this.fileErrors.length ? svgError : svgSuccess;
    },
    renderMessageAction() {
      return this.errorUpload
        ? this.$t('IMPORT_CONTACTS.ERROR_UPLOAD.MESSAGE_IMPORT_FILE.ERROR')
        : this.$t('IMPORT_CONTACTS.ERROR_UPLOAD.MESSAGE_IMPORT_FILE.SUCCESS');
    },
    agentsWallets() {
      if (this.agentList.length === 0) this.$store.dispatch('agents/get');

      const agents = this.agentList || [];
      return [
        ...[
          {
            confirmed: true,
            name: 'Nenhum',
            id: null,
            role: 'agent',
            account_id: 0,
            email: 'None',
            wallet: null,
          },
        ],
        ...agents,
      ];
    },
    noneLabelOption() {
      return {
        id: null,
        name: this.$t('IMPORT_CONTACTS.LABEL.NONE'),
      };
    },
    labelOptions() {
      const labels = (this.allLabels || []).map(label => ({
        id: label.id,
        name: label.title,
        color: label.color,
      }));

      return [this.noneLabelOption, ...labels];
    },
    csvUrl() {
      return '/downloads/import-contacts-csv-sample.csv';
    },
    xlsxUrl() {
      return '/downloads/import-contacts-xlsx-sample.xlsx';
    },
    xlsUrl() {
      return '/downloads/import-contacts-xls-sample.xls';
    },
  },
  mounted() {
    this.$track(CONTACTS_EVENTS.IMPORT_MODAL_OPEN);
    this.selectedLabel = this.noneLabelOption;
    if (!this.allLabels.length) {
      this.$store.dispatch('labels/get');
    }
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    async uploadFile() {
      if (!this.file) {
        this.showAlert(this.$t('IMPORT_CONTACTS.ERROR_UPLOAD.NO_FILE'));
        return;
      }

      if (this.isValidating) {
        this.showAlert(this.$t('IMPORT_CONTACTS.ERROR_UPLOAD.VALIDATING'));
        return;
      }

      if (this.fileErrors.length > 0) {
        this.showAlert(
          this.$t('IMPORT_CONTACTS.ERROR_UPLOAD.FIX_ERRORS', {
            count: this.fileErrors.length,
          })
        );
        return;
      }

      this.uploadAction = true;
      this.isProcessing = true;
      this.importSummary = null;
      this.errorUpload = false;

      try {
        const response = await this.$store.dispatch('contacts/import', {
          file: this.file,
          selectedWalletId: this.selectedWalletId,
          duplicateStrategy: this.duplicateStrategy,
          selectedLabelId: this.selectedLabelId,
        });
        await this.pollImportStatus(response.id);
        this.$track(CONTACTS_EVENTS.IMPORT_SUCCESS);
      } catch (error) {
        this.isProcessing = false;
        this.errorUpload = true;
        this.showAlert(
          error.message || this.$t('IMPORT_CONTACTS.ERROR_MESSAGE')
        );
        this.$track(CONTACTS_EVENTS.IMPORT_FAILURE);
      } finally {
        this.$store.dispatch('contacts/getContactViews', {
          page: this.page,
        });
      }
    },
    pollImportStatus(importId) {
      return new Promise((resolve, reject) => {
        let attempts = 0;

        this.pollingTimer = setInterval(async () => {
          attempts += 1;

          if (attempts > MAX_POLL_ATTEMPTS) {
            this.stopPolling();
            reject(new Error(this.$t('IMPORT_CONTACTS.POLLING_TIMEOUT')));
            return;
          }

          try {
            const response = await ContactAPI.getDataImportStatus(importId);
            const { status } = response.data;

            if (status === 'completed' || status === 'failed') {
              this.stopPolling();
              this.isProcessing = false;
              this.importSummary = response.data;
              resolve(response.data);
            }
          } catch (pollError) {
            this.stopPolling();
            reject(pollError);
          }
        }, POLL_INTERVAL_MS);
      });
    },
    stopPolling() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer);
        this.pollingTimer = null;
      }
    },
    handleFileUpload(event) {
      this.fileErrors = [];
      this.errorUpload = false;
      this.isValidating = false;

      const uploadedFile = event.target.files[0];
      if (!uploadedFile) {
        this.file = '';
        return;
      }

      this.file = uploadedFile;
      const fileExtension = uploadedFile.name.split('.').pop().toLowerCase();

      switch (fileExtension) {
        case 'xlsx':
        case 'xls':
          this.validateExcelFile(uploadedFile);
          break;
        case 'csv':
          this.validateCSVFile(uploadedFile);
          break;
        default:
          this.fileErrors.push(
            this.$t('IMPORT_CONTACTS.ERROR_UPLOAD.NOT_SUPPORT')
          );
          this.errorUpload = true;
      }
    },
    validateExcelFile(file) {
      this.isValidating = true;
      const reader = new FileReader();

      reader.onload = e => {
        try {
          const workbook = XLSX.read(e.target.result, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = XLSX.utils.sheet_to_json(
            workbook.Sheets[sheetName],
            { defval: '' }
          );

          this.validateData(worksheet);
        } catch (error) {
          this.fileErrors = [
            `${this.$t('IMPORT_CONTACTS.ERROR_UPLOAD.NOT_SUPPORT')} ${
              error.message || ''
            }`.trim(),
          ];
          this.errorUpload = true;
        } finally {
          this.isValidating = false;
        }
      };

      reader.onerror = () => {
        this.fileErrors = [
          this.$t('IMPORT_CONTACTS.ERROR_UPLOAD.FILE_READ_ERROR'),
        ];
        this.errorUpload = true;
        this.isValidating = false;
      };

      reader.readAsBinaryString(file);
    },
    validateCSVFile(file) {
      this.isValidating = true;

      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: results => {
          try {
            const rows = results.data || [];
            if (!rows.length && results.errors?.length) {
              const parseError = results.errors[0];
              this.fileErrors = [
                `${this.$t('IMPORT_CONTACTS.ERROR_UPLOAD.NOT_SUPPORT_CSV')} ${
                  parseError.message
                } (${this.$t('IMPORT_CONTACTS.ERROR_UPLOAD.LINE')} ${
                  (parseError.row || 0) + 2
                })`,
              ];
              this.errorUpload = true;
              return;
            }

            this.validateData(rows);
          } finally {
            this.isValidating = false;
          }
        },
        error: error => {
          this.fileErrors = [
            `${this.$t('IMPORT_CONTACTS.ERROR_UPLOAD.NOT_SUPPORT_CSV')} ${
              error.message
            }`,
          ];
          this.errorUpload = true;
          this.isValidating = false;
        },
      });
    },
    composeNameFromRow(row) {
      if (row.name) return String(row.name).trim();

      return [row.first_name, row.last_name]
        .filter(Boolean)
        .map(value => String(value).trim())
        .join(' ')
        .trim();
    },
    normalizeRowKeys(row) {
      return Object.keys(row || {}).reduce((acc, key) => {
        acc[String(key).trim().toLowerCase()] = row[key];
        return acc;
      }, {});
    },
    isHeaderRow(row) {
      const normalized = this.normalizeRowKeys(row);
      const name = String(normalized.name || '').trim().toLowerCase();
      const phone = String(normalized.phone_number || '')
        .trim()
        .toLowerCase();

      return name === 'name' && phone === 'phone_number';
    },
    lineLabel(lineNumber) {
      return `${this.$t('IMPORT_CONTACTS.ERROR_UPLOAD.LINE')} ${lineNumber}`;
    },
    validateData(data) {
      this.fileErrors = [];

      const rows = (data || [])
        .map(row => this.normalizeRowKeys(row))
        .filter(row => !this.isHeaderRow(row))
        .filter(row =>
          Object.values(row).some(
            value => value !== null && value !== undefined && String(value).trim() !== ''
          )
        );

      if (rows.length === 0) {
        this.fileErrors.push(this.$t('IMPORT_CONTACTS.ERROR_UPLOAD.EMPTY_FILE'));
        this.errorUpload = true;
        return;
      }

      const sampleKeys = Object.keys(rows[0]);
      const hasNameColumn =
        sampleKeys.includes('name') ||
        sampleKeys.includes('first_name') ||
        sampleKeys.includes('last_name');
      const hasPhoneColumn = sampleKeys.includes('phone_number');

      if (!hasNameColumn || !hasPhoneColumn) {
        const foundColumns = sampleKeys.filter(Boolean).join(', ') || '-';
        this.fileErrors.push(
          this.$t('IMPORT_CONTACTS.ERROR_UPLOAD.MISSING_COLUMNS', {
            columns: foundColumns,
          })
        );
        this.errorUpload = true;
        return;
      }

      rows.forEach((row, index) => {
        // +2: spreadsheet line 1 is header, data starts at line 2
        const lineNumber = index + 2;
        const name = this.composeNameFromRow(row);
        const phoneNumber = row.phone_number;

        if (!name) {
          this.fileErrors.push(
            `${this.lineLabel(lineNumber)}: ${this.$t(
              'IMPORT_CONTACTS.ERROR_UPLOAD.ERROR_NAME'
            )}`
          );
        }

        if (
          phoneNumber === null ||
          phoneNumber === undefined ||
          String(phoneNumber).trim() === ''
        ) {
          this.fileErrors.push(
            `${this.lineLabel(lineNumber)}: ${this.$t(
              'IMPORT_CONTACTS.ERROR_UPLOAD.ERROR_PHONE'
            )}`
          );
          return;
        }

        if (!this.validatePhoneNumber(phoneNumber)) {
          this.fileErrors.push(
            `${this.lineLabel(lineNumber)}: ${this.$t(
              'IMPORT_CONTACTS.ERROR_UPLOAD.CORRECT_PHONE_FORMAT',
              { phone: String(phoneNumber).trim() }
            )}`
          );
        }
      });

      this.errorUpload = this.fileErrors.length > 0;
    },
    validatePhoneNumber(phoneNumber) {
      const cleanedNumber = String(phoneNumber).replace(/\D/g, '');
      const regex = /^(\d{2,4})(\d{2,3})(\d{8,9})$/;

      return regex.test(cleanedNumber);
    },
    getAgentWalletId(agent) {
      this.selectedWalletId = agent.id ? agent.id : null;
      this.agentSelectedWallet = agent;
    },
    onLabelSelect(label) {
      this.selectedLabelId = label.id || null;
      this.selectedLabel = label;
    },
  },
};
</script>

<style scoped>
.error-container {
  margin-top: 10px;
  margin-bottom: 10px;
}

.error-title {
  color: red;
  font-weight: bold;
  margin-bottom: 5px;
  padding: 6px 33px;
}

.error-list {
  list-style-type: disc;
  padding-left: 20px;
}

.error-item {
  color: red;
  margin-bottom: 5px;
}
.content-box {
  overflow: unset;
}

.modal-footer {
  margin-top: 3rem;
}
.card-input-file {
  font-size: 1.4rem;
}
.card-input-file input {
  width: 80%;
}

.validation-status {
  margin-top: var(--space-smaller);
  color: var(--s-600);
  font-size: var(--font-size-mini);
}

.validation-errors {
  margin-top: var(--space-normal);
  padding: var(--space-small);
  border: 1px solid var(--r-200);
  border-radius: var(--border-radius-normal);
  background: var(--r-50);
}

.validation-errors-title {
  color: var(--r-700);
  font-weight: 700;
  margin: 0 0 var(--space-smaller);
}

.validation-errors-count {
  font-weight: 600;
}

.validation-errors-list {
  list-style-type: disc;
  margin: 0;
  padding-left: var(--space-normal);
  max-height: 180px;
  overflow-y: auto;
}

.validation-errors-item {
  color: var(--r-700);
  margin-bottom: 4px;
  font-size: var(--font-size-mini);
  word-break: break-word;
}

.validation-errors-more,
.validation-errors-hint {
  margin: var(--space-smaller) 0 0;
  color: var(--r-600);
  font-size: var(--font-size-mini);
}

.validation-errors-hint {
  font-weight: 600;
}
.container-icons {
  font-weight: 600;

  img {
    display: flex;
    width: 15%;
    height: 15%;
    margin: 56px auto 0;
  }

  margin-bottom: 10px;
}

.container-message {
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
  align-content: center;
  align-items: center;
  margin: 22px 0;
}

.error-content {
  word-wrap: break-word;
  display: flex;
  margin-top: var(--space-small);
  justify-content: center;
  align-content: center;
  flex-direction: column;
  align-items: center;
}

.processing-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--space-large) 0;
}

.processing-message {
  margin-top: var(--space-normal);
  text-align: center;
}

.duplicate-strategy {
  margin-top: var(--space-normal);
}

.import-label-section {
  margin-top: var(--space-normal);
}

.import-label-header {
  display: flex;
  align-items: center;
  gap: var(--space-smaller);
  margin-bottom: var(--space-smaller);
}

.import-label-optional {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--s-75);
  color: var(--s-600);
  font-size: var(--font-size-mini);
  font-weight: 500;
  line-height: 1.4;
}

.import-label-help {
  display: flex;
  align-items: flex-start;
  gap: var(--space-micro);
  margin: var(--space-smaller) 0 0;
  color: var(--s-600);
  font-size: var(--font-size-mini);
  line-height: 1.4;
}

.import-label-help-icon {
  flex-shrink: 0;
  margin-top: 2px;
  color: var(--s-500);
}

.duplicate-strategy-label {
  display: block;
  margin-bottom: var(--space-smaller);
}

.duplicate-strategy-options {
  display: flex;
  flex-direction: column;
  gap: var(--space-small);
}

.duplicate-strategy-option {
  display: flex;
  align-items: flex-start;
  gap: var(--space-small);
  padding: var(--space-small);
  border: 1px solid var(--s-100);
  border-radius: var(--border-radius-normal);
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;

  input {
    margin-top: 3px;
  }

  &--selected {
    border-color: var(--w-500);
    background-color: var(--w-50);
  }
}

.duplicate-strategy-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.duplicate-strategy-title {
  font-weight: 600;
  font-size: var(--font-size-small);
}

.duplicate-strategy-desc {
  font-size: var(--font-size-mini);
  color: var(--s-600);
}

.summary-container {
  padding: var(--space-medium) var(--space-normal) var(--space-large);
}

.summary-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: var(--space-medium);
}

.summary-icon {
  margin-bottom: var(--space-small);
}

.summary-icon--success {
  color: var(--g-600);
}

.summary-icon--warning {
  color: var(--y-600);
}

.summary-icon--failed {
  color: var(--r-600);
}

.summary-title {
  font-size: var(--font-size-medium);
  font-weight: 600;
  margin: 0 0 var(--space-micro);
}

.summary-subtitle {
  font-size: var(--font-size-small);
  color: var(--s-600);
  margin: 0;
}

.summary-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-small);
  margin-bottom: var(--space-medium);
}

.summary-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--space-small);
  border-radius: var(--border-radius-normal);
  background: var(--s-50);
  border: 1px solid var(--s-100);
}

.summary-stat-value {
  font-size: 2.4rem;
  font-weight: 700;
  line-height: 1.2;
}

.summary-stat-label {
  font-size: var(--font-size-mini);
  color: var(--s-600);
  text-align: center;
  margin-top: var(--space-micro);
}

.summary-stat--success .summary-stat-value {
  color: var(--g-600);
}

.summary-stat--info .summary-stat-value {
  color: var(--w-600);
}

.summary-stat--neutral .summary-stat-value {
  color: var(--s-600);
}

.summary-stat--danger .summary-stat-value {
  color: var(--r-600);
}

.summary-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-small);
}

.summary-download-link {
  font-size: var(--font-size-small);
  font-weight: 600;
}

.summary-close {
  margin-top: var(--space-micro);
}
</style>
